import { createSlice } from '@reduxjs/toolkit';
import {
  handleSetSelectedFilterIds,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterValue,
} from 'store/helpers/filterHelpers';
import {
  instagramCommentInitialState,
  instagramInitialState,
  newsInitialState,
  newspaperInitialState,
  publicInitialState,
  telegramInitialState,
  twitterInitialState,
  nativePlatformsInitialState,
} from './initialState';
import {
  addCategoryResources,
  createResourceCategory,
  deleteResourceCategory,
  fetchCategoryManage,
  fetchResourceCategories,
  fetchResourceCategoryResources,
  removeCategoryResources,
  searchCategorySources,
} from './resourceCategory.actions';
import {
  RESOURCE_FETCH_TARGET,
  SEARCH_STATUS,
  getCategoryApiPath,
  mergeManagePage,
  toCategoryEntry,
} from './resourceCategory.adapter';

const filtersInitialState = {
  public: publicInitialState,
  news: newsInitialState,
  instagram: instagramInitialState,
  instagramComment: instagramCommentInitialState,
  telegram: telegramInitialState,
  twitter: twitterInitialState,
  newspaper: newspaperInitialState,
  nativePlatforms: nativePlatformsInitialState,
};

const sourceSearchInitialState = {
  items: [],
  total: null,
  page: 1,
  lastPageSize: 0,
  status: SEARCH_STATUS.IDLE,
  error: null,
  requestId: null,
};

const emptyPlatformList = () => ({
  items: [],
  total: 0,
  itemCount: undefined,
  loadedCount: 0,
  status: SEARCH_STATUS.IDLE,
  error: null,
  requestId: null,
});

const sourceListInitialState = {
  byPlatform: {},
};

const platformList = (state, frontPlatform) => {
  if (!state.sourceList.byPlatform[frontPlatform]) {
    state.sourceList.byPlatform[frontPlatform] = emptyPlatformList();
  }
  return state.sourceList.byPlatform[frontPlatform];
};

const manageInitialState = {
  id: null,
  recordId: null,
  name: '',
  description: '',
  nameError: null,
  loading: false,
  importing: false,
  error: null,
  resources: {},
  totals: {},
  infoCounts: {},
  platformLimits: undefined,
  resourcesCount: undefined,
  pages: {},
  loadingTabs: {},
  loadingMore: {},
  resourceErrors: {},
  savedName: '',
  savedDescription: '',
};

const isOtherManageTag = (state, id) => String(state.manage.id) !== String(id);

const applyManageInfo = (state, info) => {
  state.manage.recordId = info.id;
  state.manage.name = info.name ?? '';
  state.manage.description = info.description || '';
  state.manage.savedName = info.name ?? '';
  state.manage.savedDescription = info.description || '';
  state.manage.infoCounts = info.platforms || {};
  state.manage.platformLimits = info.platform_limits;
  state.manage.resourcesCount = info.items_count;
};

/**
 * Keeps the open tag's counters in step with an add/remove response:
 * `items_count` is the new total, `delta` moves the tab's platform count.
 */
const applyManageCountChange = (state, { id, frontPlatform }, total, delta) => {
  if (isOtherManageTag(state, id)) return;
  state.manage.resourcesCount = total;
  if (!frontPlatform || !delta) return;
  const key = getCategoryApiPath(frontPlatform);
  const current = Number(state.manage.infoCounts[key]) || 0;
  state.manage.infoCounts[key] = Math.max(0, current + delta);
};

const initialState = {
  filters: filtersInitialState,
  selectedPlatformForResources: 'news',
  list: {
    data: [],
    total: 0,
    page: 1,
    loading: false,
    error: null,
  },
  manage: manageInitialState,
  sourceSearch: sourceSearchInitialState,
  sourceList: sourceListInitialState,
};

export const resourceCategorySlice = createSlice({
  name: 'resourceCategorySlice',
  initialState,
  reducers: {
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(state, action, filtersInitialState);
    },
    changeSortBy: (state, action) => {
      const { componentName, parentPlatform, order, selected } = action.payload;
      const sort = state.filters[parentPlatform][componentName];
      if (order) {
        sort.order = order;
      }
      if (selected) {
        sort.selected = selected;
      }
    },
    resetAllFilters: (state) => {
      state.filters = filtersInitialState;
    },
    setSelectedPlatformForResources: (state, action) => {
      state.selectedPlatformForResources = action.payload;
    },
    toggleFilterActivateSwitch: (state, action) => {
      const { componentName } = action.payload;
      const filter =
        state.filters[state.selectedPlatformForResources][componentName];
      filter.isDisabled = !filter.isDisabled;
    },
    resetManage: (state) => {
      state.manage = manageInitialState;
    },
    setManageName: (state, action) => {
      state.manage.name = action.payload;
      state.manage.nameError = null;
    },
    setManageDescription: (state, action) => {
      state.manage.description = action.payload;
    },
    setManageNameError: (state, action) => {
      state.manage.nameError = action.payload;
    },
    setManageId: (state, action) => {
      state.manage.id = action.payload;
    },
    setManageInfo: (state, action) => {
      applyManageInfo(state, action.payload);
    },
    markManageInfoSaved: (state) => {
      state.manage.savedName = state.manage.name;
      state.manage.savedDescription = state.manage.description;
      state.manage.nameError = null;
    },
    setManageImporting: (state, action) => {
      state.manage.importing = action.payload;
    },
    prependManageEntry: (state, action) => {
      const { platform, item } = action.payload;
      const entry = toCategoryEntry(platform, item, {
        isLocal: true,
        addedAt: Date.now(),
      });
      const list = state.manage.resources[platform] || [];
      state.manage.resources[platform] = [
        entry,
        ...list.filter((row) => row.key !== entry.key),
      ];
      state.manage.totals[platform] = (state.manage.totals[platform] || 0) + 1;
    },
    resetManageResources: (state) => {
      state.manage.resources = {};
      state.manage.totals = {};
      state.manage.pages = {};
      state.manage.loadingTabs = {};
      state.manage.loadingMore = {};
      state.manage.resourceErrors = {};
    },
    resetManageTab: (state, action) => {
      const platform = action.payload;
      delete state.manage.resources[platform];
      delete state.manage.totals[platform];
      delete state.manage.pages[platform];
      delete state.manage.loadingTabs[platform];
      delete state.manage.loadingMore[platform];
      delete state.manage.resourceErrors[platform];
    },
    resetSourceSearch: (state) => {
      state.sourceSearch = sourceSearchInitialState;
    },
    resetSourceList: (state) => {
      state.sourceList = sourceListInitialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResourceCategories.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchResourceCategories.fulfilled, (state, action) => {
        state.list.data = action.payload.data;
        state.list.total = action.payload.total;
        state.list.page = action.payload.page;
        state.list.loading = false;
        state.list.error = null;
      })
      .addCase(fetchResourceCategories.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload;
      })
      .addCase(deleteResourceCategory.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (category) => category.id !== action.payload
        );
      })
      .addCase(createResourceCategory.fulfilled, (state, action) => {
        if (action.payload) {
          state.list.data = [action.payload, ...state.list.data];
        }
      })
      .addCase(fetchCategoryManage.pending, (state) => {
        state.manage.loading = true;
        state.manage.error = null;
      })
      .addCase(fetchCategoryManage.fulfilled, (state, action) => {
        if (isOtherManageTag(state, action.meta.arg.id)) return;
        applyManageInfo(state, action.payload);
        state.manage.loading = false;
      })
      .addCase(fetchCategoryManage.rejected, (state, action) => {
        if (isOtherManageTag(state, action.meta.arg.id)) return;
        state.manage.loading = false;
        state.manage.error = action.payload;
      })
      .addCase(addCategoryResources.fulfilled, (state, action) => {
        applyManageCountChange(
          state,
          action.meta.arg,
          action.payload.items_count,
          action.payload.added
        );
      })
      .addCase(removeCategoryResources.fulfilled, (state, action) => {
        applyManageCountChange(
          state,
          action.meta.arg,
          action.payload.items_count,
          -action.payload.removed
        );
      })
      .addCase(searchCategorySources.pending, (state, action) => {
        const { page = 1 } = action.meta.arg;
        state.sourceSearch.requestId = action.meta.requestId;
        if (page === 1) {
          state.sourceSearch.items = [];
          state.sourceSearch.total = null;
          state.sourceSearch.lastPageSize = 0;
        }
        state.sourceSearch.status = SEARCH_STATUS.LOADING;
        state.sourceSearch.error = null;
      })
      .addCase(searchCategorySources.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.sourceSearch.requestId) return;
        const { page = 1 } = action.meta.arg;
        const items = action.payload?.data ?? [];
        state.sourceSearch.items =
          page === 1 ? items : [...state.sourceSearch.items, ...items];
        const parsedTotal = Number(action.payload?.total);
        state.sourceSearch.total = Number.isFinite(parsedTotal)
          ? parsedTotal
          : undefined;
        state.sourceSearch.lastPageSize = items.length;
        state.sourceSearch.page = page;
        state.sourceSearch.status = SEARCH_STATUS.SUCCESS;
      })
      .addCase(searchCategorySources.rejected, (state, action) => {
        if (action.meta.requestId !== state.sourceSearch.requestId) return;
        state.sourceSearch.status = SEARCH_STATUS.ERROR;
        state.sourceSearch.error = action.payload;
      })
      .addCase(fetchResourceCategoryResources.pending, (state, action) => {
        if (action.meta.arg.target === RESOURCE_FETCH_TARGET.MANAGE) {
          const { frontPlatform, page = 1 } = action.meta.arg;
          if (page > 1) state.manage.loadingMore[frontPlatform] = true;
          else state.manage.loadingTabs[frontPlatform] = true;
          return;
        }
        if (action.meta.arg.target !== RESOURCE_FETCH_TARGET.LIST) return;
        const { page = 1, frontPlatform } = action.meta.arg;
        if (!frontPlatform) return;
        const bucket = platformList(state, frontPlatform);
        bucket.requestId = action.meta.requestId;
        if (page === 1) {
          bucket.items = [];
          bucket.loadedCount = 0;
        }
        bucket.status = SEARCH_STATUS.LOADING;
        bucket.error = null;
      })
      .addCase(fetchResourceCategoryResources.fulfilled, (state, action) => {
        if (action.meta.arg.target === RESOURCE_FETCH_TARGET.MANAGE) {
          if (isOtherManageTag(state, action.meta.arg.id)) return;
          const { frontPlatform, page = 1 } = action.meta.arg;
          const next = mergeManagePage({
            resources: state.manage.resources,
            totals: state.manage.totals,
            pages: state.manage.pages,
            frontPlatform,
            page,
            data: action.payload?.data,
            total: action.payload?.total,
          });
          state.manage.resources = next.resources;
          state.manage.totals = next.totals;
          state.manage.pages = next.pages;
          delete state.manage.loadingTabs[frontPlatform];
          delete state.manage.loadingMore[frontPlatform];
          delete state.manage.resourceErrors[frontPlatform];
          return;
        }
        if (action.meta.arg.target !== RESOURCE_FETCH_TARGET.LIST) return;
        const { page = 1, frontPlatform } = action.meta.arg;
        if (!frontPlatform) return;
        const bucket = platformList(state, frontPlatform);
        if (action.meta.requestId !== bucket.requestId) return;
        const pageItems = action.payload?.data ?? [];
        if (page === 1) {
          bucket.items = pageItems;
        } else {
          const incoming = new Map(
            pageItems.map((item) => [String(item.id), item])
          );
          const seen = new Set();
          const refreshed = bucket.items.map((item) => {
            const next = incoming.get(String(item.id));
            if (!next) return item;
            seen.add(String(item.id));
            return next;
          });
          bucket.items = [
            ...refreshed,
            ...pageItems.filter((item) => !seen.has(String(item.id))),
          ];
        }
        bucket.total = action.payload?.total ?? 0;
        if (page === 1 && !action.meta.arg.q) {
          bucket.itemCount = action.payload?.total ?? 0;
        }
        bucket.loadedCount =
          page === 1 ? pageItems.length : bucket.loadedCount + pageItems.length;
        bucket.status = SEARCH_STATUS.SUCCESS;
      })
      .addCase(fetchResourceCategoryResources.rejected, (state, action) => {
        if (action.meta.arg.target === RESOURCE_FETCH_TARGET.MANAGE) {
          const { frontPlatform } = action.meta.arg;
          delete state.manage.loadingTabs[frontPlatform];
          delete state.manage.loadingMore[frontPlatform];
          state.manage.resourceErrors[frontPlatform] = true;
          return;
        }
        if (action.meta.arg.target !== RESOURCE_FETCH_TARGET.LIST) return;
        const { frontPlatform } = action.meta.arg;
        if (!frontPlatform) return;
        const bucket = platformList(state, frontPlatform);
        if (action.meta.requestId !== bucket.requestId) return;
        bucket.status = SEARCH_STATUS.ERROR;
        bucket.error = action.payload;
      });
  },
});

export const {
  setSelectedPlatformForResources,
  setSelectedFilterValue,
  setSelectedFilterIds,
  resetAllFilters,
  setSelectedContentBasedValues,
  changeSortBy,
  toggleFilterActivateSwitch,
  resetManage,
  setManageName,
  setManageDescription,
  setManageNameError,
  setManageId,
  setManageInfo,
  markManageInfoSaved,
  setManageImporting,
  prependManageEntry,
  resetManageResources,
  resetManageTab,
  resetSourceSearch,
  resetSourceList,
} = resourceCategorySlice.actions;
